import { FormEvent, useState } from "react";

type SubmissionState = {
  kind: "idle" | "submitting" | "success" | "error";
  message?: string;
};

type ContactFormProps = {
  defaultInquiryType?: string;
  lockInquiryType?: boolean;
  defaultMessage?: string;
};

export function ContactForm({
  defaultInquiryType = "",
  lockInquiryType = false,
  defaultMessage = "",
}: ContactFormProps) {
  const [submission, setSubmission] = useState<SubmissionState>({
    kind: "idle",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    setSubmission({ kind: "submitting", message: "送信しています…" });

    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => ({}))) as {
        message?: string;
      };

      if (!response.ok) {
        throw new Error(result.message || "送信に失敗しました。");
      }

      form.reset();
      setSubmission({
        kind: "success",
        message: result.message || "お問い合わせを受け付けました。",
      });
    } catch (error) {
      setSubmission({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "送信に失敗しました。時間をおいてお試しください。",
      });
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <label>
          <span>
            ご担当者名 <em>必須</em>
          </span>
          <input
            name="name"
            type="text"
            autoComplete="name"
            required
            maxLength={100}
          />
        </label>
        <label>
          <span>
            メールアドレス <em>必須</em>
          </span>
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
          />
        </label>
      </div>
      <label>
        <span>
          お問い合わせ種別 <em>必須</em>
        </span>
        {lockInquiryType && defaultInquiryType ? (
          <>
            <select
              className="contact-select-locked"
              value={defaultInquiryType}
              aria-disabled="true"
              disabled
            >
              <option value={defaultInquiryType}>{defaultInquiryType}</option>
            </select>
            <input
              type="hidden"
              name="inquiry_type"
              value={defaultInquiryType}
              readOnly
            />
          </>
        ) : (
          <select
            name="inquiry_type"
            defaultValue={defaultInquiryType}
            required
          >
            <option value="" disabled>
              選択してください
            </option>
            <option value="AIYSSについて">AIYSSについて</option>
            <option value="年間リーグについて">年間リーグについて</option>
            <option value="Job Questについて">Job Questについて</option>
            <option value="活動・取材について">活動・取材について</option>
            <option value="スポンサー協賛について">
              スポンサー協賛について
            </option>
            <option value="企業との連携">企業との連携</option>
            <option value="その他">その他</option>
          </select>
        )}
      </label>
      <label>
        <span>所属（任意）</span>
        <input
          name="organization"
          type="text"
          autoComplete="organization"
          maxLength={160}
          placeholder="学校名・会社名など"
        />
      </label>
      <label>
        <span>
          お問い合わせ内容 <em>必須</em>
        </span>
        <textarea
          name="message"
          defaultValue={defaultMessage}
          rows={5}
          required
          minLength={10}
          maxLength={4000}
        />
      </label>
      <div className="form-submit-row">
        <button
          className="contact-button"
          type="submit"
          disabled={submission.kind === "submitting"}
        >
          {submission.kind === "submitting" ? "送信中…" : "送信する"}{" "}
          <span aria-hidden="true">→</span>
        </button>
        {submission.message && (
          <p
            className={`form-status is-${submission.kind}`}
            role={submission.kind === "error" ? "alert" : "status"}
          >
            {submission.message}
          </p>
        )}
      </div>
      <p className="form-note">
        送信内容は、お問い合わせへの対応にのみ使用します。
      </p>
    </form>
  );
}
